import React, { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { apiFetch, parseJsonSafe } from '../utils/api';
import TaskAttachments from '../components/TaskAttachments';
import ExpandableText from '../components/ExpandableText';
import './ProjectBoard.css';

const statusOptions = [
  { value: 'a_la_espera', label: 'A la espera' },
  { value: 'en_proceso', label: 'En proceso' },
  { value: 'finalizado', label: 'Finalizado' }
];
const tipoOptions = [
  { value: 'cliente', label: 'Cliente' },
  { value: 'interno', label: 'Interno' }
];
const prioridadOptions = [
  { value: 'alta', label: 'Alta' },
  { value: 'media', label: 'Media' },
  { value: 'baja', label: 'Baja' }
];

const importanceOptions = [
  { value: 'alta', label: 'Alta' },
  { value: 'media', label: 'Media' },
  { value: 'baja', label: 'Baja' }
];
const taskEstadoOptions = [
  { value: 'pendiente', label: 'Pendiente' },
  { value: 'en_proceso', label: 'En proceso' },
  { value: 'realizada', label: 'Realizada' }
];
const taskBadgeColor = { alta: 'danger', media: 'warning', baja: 'secondary' };
const estadoBadgeClass = { pendiente: 'bg-light text-dark', en_proceso: 'bg-info text-dark', realizada: 'bg-success' };
const estadoLabel = { pendiente: 'Pendiente', en_proceso: 'En proceso', realizada: 'Realizada' };
const TASK_STATES = ['pendiente', 'en_proceso', 'realizada'];
const importanceBorder = { alta: '#dc3545', media: '#fd7e14', baja: '#ffc107' };
const workloadOptions = Array.from({ length: 8 }).map((_, i) => ({ value: i + 1, label: String(i + 1) }));

const todayStr = () => {
  const d = new Date();
  const tz = d.getTimezoneOffset();
  return new Date(d.getTime() - tz * 60000).toISOString().slice(0, 10);
};
const formatBytes = (size) => (size ? `${Math.round((Number(size) / 1024) * 10) / 10} KB` : '');
const canPreview = (mime = '') => {
  const m = String(mime || '').toLowerCase();
  return m.startsWith('image/') || m === 'application/pdf' || m.startsWith('text/');
};
const formatDate = (d) => {
  if (!d) return 'Sin fecha';
  const s = String(d).slice(0, 10);
  const [y, m, day] = s.split('-');
  return (y && m && day) ? `${day}/${m}/${y}` : s;
};

// Tarjeta de un seguimiento, con gestión propia de adjuntos
const FollowupCard = ({ followup, token, downloadFile, onPreview, onEdit, onDelete, onChanged, onUnauthorized }) => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState('');
  const [err, setErr] = useState('');
  const [inputKey, setInputKey] = useState(0);
  const [visBusy, setVisBusy] = useState(false);
  const [attVisBusyId, setAttVisBusyId] = useState(null);

  const toggleAttachmentVisible = async (att) => {
    setAttVisBusyId(att.id);
    try {
      const res = await apiFetch(`/api/projects/followup-attachments/${att.id}/visibility`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ visible_cliente: !att.visible_cliente })
      });
      if (res.status === 401) return onUnauthorized();
      if (!res.ok) { const d = await parseJsonSafe(res); throw new Error((d && d.message) || 'No se pudo actualizar la visibilidad'); }
      onChanged();
    } catch (e) {
      alert(e.message);
    } finally {
      setAttVisBusyId(null);
    }
  };

  const toggleVisible = async () => {
    setVisBusy(true);
    try {
      const res = await apiFetch(`/api/projects/followups/${followup.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ titulo: followup.titulo, fecha: followup.fecha, notas: followup.notas, visible_cliente: !followup.visible_cliente })
      });
      if (res.status === 401) return onUnauthorized();
      if (!res.ok) { const d = await parseJsonSafe(res); throw new Error((d && d.message) || 'No se pudo actualizar la visibilidad'); }
      onChanged();
    } catch (e) {
      alert(e.message);
    } finally {
      setVisBusy(false);
    }
  };

  const upload = async () => {
    if (!files.length) return;
    setUploading(true);
    setErr('');
    // Secuencial: la primera subida crea la carpeta de Drive del proyecto.
    const errores = [];
    try {
      for (let i = 0; i < files.length; i += 1) {
        const file = files[i];
        setProgress(`Subiendo ${i + 1}/${files.length}...`);
        try {
          const fd = new FormData();
          fd.append('file', file);
          const res = await apiFetch(`/api/projects/followups/${followup.id}/attachments`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
            body: fd
          });
          if (res.status === 401) return onUnauthorized();
          if (!res.ok) {
            const data = await parseJsonSafe(res);
            throw new Error((data && data.message) || 'No se pudo subir');
          }
        } catch (e) {
          errores.push(`${file.name}: ${e.message}`);
        }
      }
      setFiles([]);
      setInputKey((k) => k + 1);
      if (errores.length) setErr(`No se pudieron subir ${errores.length} archivo(s): ${errores.join('; ')}`);
      onChanged();
    } finally {
      setUploading(false);
      setProgress('');
    }
  };

  const removeAttachment = async (attId) => {
    if (!window.confirm('¿Eliminar este adjunto?')) return;
    try {
      const res = await apiFetch(`/api/projects/followup-attachments/${attId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.status === 401) return onUnauthorized();
      if (!res.ok) {
        const d = await parseJsonSafe(res);
        throw new Error((d && d.message) || 'No se pudo eliminar el adjunto');
      }
      onChanged();
    } catch (e) {
      alert(e.message);
    }
  };

  const attachments = Array.isArray(followup.attachments) ? followup.attachments : [];

  return (
    <div className="card shadow-sm mb-3">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <h6 className="mb-1 fw-bold">{followup.titulo}</h6>
            <div className="text-muted small mb-2">
              <i className="bi bi-calendar3" /> {formatDate(followup.fecha)}
            </div>
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                id={`fvis-${followup.id}`}
                checked={followup.visible_cliente === true}
                disabled={visBusy}
                onChange={toggleVisible}
              />
              <label className="form-check-label small" htmlFor={`fvis-${followup.id}`}>
                {followup.visible_cliente
                  ? <span className="text-success">👁 Visible para el cliente</span>
                  : <span className="text-muted">Solo interno</span>}
              </label>
            </div>
          </div>
          <div className="btn-group btn-group-sm">
            <button className="btn btn-outline-secondary" type="button" onClick={() => onEdit(followup)}>Editar</button>
            <button className="btn btn-outline-danger" type="button" onClick={() => onDelete(followup.id)}>Eliminar</button>
          </div>
        </div>
        {followup.notas ? (
          <div className="mb-2">
            <ExpandableText text={followup.notas} title={followup.titulo} />
          </div>
        ) : null}
        <div className="border-top pt-2 mt-2">
          <div className="text-muted small mb-1">Adjuntos</div>
          {attachments.length > 0 ? (
            <ul className="list-unstyled mb-2">
              {attachments.map((a) => (
                <li key={a.id} className="d-flex justify-content-between align-items-center py-1">
                  <span className="text-truncate me-2">
                    {a.filename} <span className="text-muted small">· {formatBytes(a.size)}</span>
                  </span>
                  <span className="d-flex align-items-center gap-2 flex-shrink-0">
                    <span className="form-check form-switch m-0" title="Visible en el área de cliente">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id={`favis-${a.id}`}
                        checked={a.visible_cliente === true}
                        disabled={attVisBusyId === a.id}
                        onChange={() => toggleAttachmentVisible(a)}
                      />
                      <label className="form-check-label small text-muted" htmlFor={`favis-${a.id}`}>Área cliente</label>
                    </span>
                    <span className="btn-group btn-group-sm">
                      {canPreview(a.mime_type) && (
                        <button
                          className="btn btn-outline-secondary"
                          type="button"
                          onClick={() => onPreview({ downloadUrl: `/api/projects/followup-attachments/${a.id}/download`, mime: a.mime_type, filename: a.filename })}
                        >
                          Ver
                        </button>
                      )}
                      <button
                        className="btn btn-outline-primary"
                        type="button"
                        onClick={() => downloadFile(`/api/projects/followup-attachments/${a.id}/download`, a.filename)}
                      >
                        Descargar
                      </button>
                      <button className="btn btn-outline-danger" type="button" onClick={() => removeAttachment(a.id)}>
                        Eliminar
                      </button>
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-muted small mb-2">Sin adjuntos.</div>
          )}
          <div className="d-flex gap-2 align-items-center">
            <input
              key={inputKey}
              type="file"
              multiple
              className="form-control form-control-sm"
              onChange={(e) => setFiles(Array.from(e.target.files || []))}
            />
            <button className="btn btn-sm btn-outline-primary flex-shrink-0" type="button" disabled={!files.length || uploading} onClick={upload}>
              {uploading ? (progress || 'Subiendo...') : (files.length > 1 ? `Adjuntar ${files.length}` : 'Adjuntar')}
            </button>
          </div>
          {err && <div className="text-danger small mt-1">{err}</div>}
        </div>
      </div>
    </div>
  );
};

const ProjectFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const [project, setProject] = useState(null);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('info');
  const [form, setForm] = useState({
    titulo: '',
    client_id: null,
    estado: 'a_la_espera',
    fecha_inicio: '',
    fecha_fin: '',
    comentarios: '',
    assigned_user_ids: [],
    tipo: 'cliente',
    prioridad: 'media',
    presupuesto: '',
    notas: '',
    links: '',
    visible_cliente: true
  });

  const [attachments, setAttachments] = useState([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [attachmentsError, setAttachmentsError] = useState('');
  const [attachmentUploading, setAttachmentUploading] = useState(false);
  const [attachmentFiles, setAttachmentFiles] = useState([]);
  const [attachmentProgress, setAttachmentProgress] = useState('');
  const [attachmentInputKey, setAttachmentInputKey] = useState(0);
  const [preview, setPreview] = useState({ open: false, downloadUrl: '', url: '', mime: '', filename: '', loading: false });

  // Seguimiento
  const [followups, setFollowups] = useState([]);
  const [followupsLoading, setFollowupsLoading] = useState(false);
  const [followupsError, setFollowupsError] = useState('');
  const [showFollowupForm, setShowFollowupForm] = useState(false);
  const [editingFollowupId, setEditingFollowupId] = useState(null);
  const [followupForm, setFollowupForm] = useState({ titulo: '', fecha: todayStr(), notas: '', visible_cliente: false });
  const [followupSaving, setFollowupSaving] = useState(false);

  // Tareas
  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [tasksError, setTasksError] = useState('');
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [taskSaving, setTaskSaving] = useState(false);
  const [taskForm, setTaskForm] = useState({
    titulo: '', importance: null, estado: 'pendiente', user_id: null, workload: null, client_id: null, comentarios: ''
  });
  const [taskView, setTaskView] = useState('list');

  const handleUnauthorized = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('userEmail');
    window.location.href = '/login';
  };

  const downloadFile = async (endpoint, filename) => {
    try {
      const res = await apiFetch(endpoint, { headers: { Authorization: `Bearer ${token}` } });
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) {
        const data = await parseJsonSafe(res);
        throw new Error((data && data.message) || 'Error descargando archivo');
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'archivo';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.message);
    }
  };

  const fetchProject = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiFetch(`/api/projects/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo cargar el proyecto');
      setProject(data);
      setForm({
        titulo: data?.titulo || '',
        client_id: data?.client_id || null,
        estado: data?.estado || 'a_la_espera',
        fecha_inicio: data?.fecha_inicio ? data.fecha_inicio.slice(0, 10) : '',
        fecha_fin: data?.fecha_fin ? data.fecha_fin.slice(0, 10) : '',
        comentarios: data?.comentarios || '',
        assigned_user_ids: Array.isArray(data?.assigned_users) ? data.assigned_users.map((u) => u.id) : [],
        tipo: data?.tipo || 'cliente',
        prioridad: data?.prioridad || 'media',
        presupuesto: data?.presupuesto ?? '',
        notas: data?.notas || '',
        links: data?.links || '',
        visible_cliente: data?.visible_cliente !== false
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const all = [];
      let page = 1;
      const requestedPageSize = 100;
      while (true) {
        const res = await apiFetch(`/api/clients?page=${page}&pageSize=${requestedPageSize}`, { headers: { Authorization: `Bearer ${token}` } });
        const data = await parseJsonSafe(res);
        if (res.status === 401) return handleUnauthorized();
        if (!res.ok) break;
        const items = data.data || data.items || [];
        all.push(...items);
        const total = Number(data.total || 0);
        const serverPageSize = Number(data.pageSize || requestedPageSize);
        if (!items.length) break;
        if (total > 0 && all.length >= total) break;
        if (items.length < serverPageSize) break;
        page += 1;
      }
      setClients(all);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await apiFetch('/api/users', { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.ok && Array.isArray(data)) setUsers(data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchAttachments = async () => {
    setAttachmentsLoading(true);
    setAttachmentsError('');
    try {
      const res = await apiFetch(`/api/projects/${id}/attachments`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'Error cargando adjuntos');
      setAttachments(Array.isArray(data) ? data : []);
    } catch (err) {
      setAttachments([]);
      setAttachmentsError(err.message);
    } finally {
      setAttachmentsLoading(false);
    }
  };

  const fetchFollowups = async () => {
    setFollowupsLoading(true);
    setFollowupsError('');
    try {
      const res = await apiFetch(`/api/projects/${id}/followups`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'Error cargando seguimientos');
      setFollowups(Array.isArray(data) ? data : []);
    } catch (err) {
      setFollowups([]);
      setFollowupsError(err.message);
    } finally {
      setFollowupsLoading(false);
    }
  };

  const fetchTasks = async () => {
    setTasksLoading(true);
    setTasksError('');
    try {
      const res = await apiFetch(`/api/projects/${id}/tasks`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'Error cargando tareas');
      setTasks(Array.isArray(data) ? data : []);
    } catch (err) {
      setTasks([]);
      setTasksError(err.message);
    } finally {
      setTasksLoading(false);
    }
  };

  useEffect(() => {
    fetchProject();
    fetchClients();
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (project?.id) {
      fetchAttachments();
      fetchFollowups();
      fetchTasks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id]);

  const clientOptions = useMemo(
    () => clients.map((c) => ({ value: c.id, label: c.razon_social || c.email || `Cliente ${c.id}` })),
    [clients]
  );
  const userOptions = useMemo(
    () => users.map((u) => ({ value: u.id, label: u.name || u.email })),
    [users]
  );

  const saveProject = async (e) => {
    e.preventDefault();
    if (!form.titulo) {
      setError('El título es obligatorio');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = { ...form };
      const res = await apiFetch(`/api/projects/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo guardar el proyecto');
      setProject(data);
      alert('Proyecto guardado');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const uploadAttachment = async () => {
    if (!attachmentFiles.length) return;
    setAttachmentUploading(true);
    setAttachmentsError('');
    // Subida secuencial (uno tras otro) a propósito: la primera subida crea la
    // carpeta de Drive del proyecto; hacerlo en paralelo podría crear duplicadas.
    const errores = [];
    try {
      for (let i = 0; i < attachmentFiles.length; i += 1) {
        const file = attachmentFiles[i];
        setAttachmentProgress(`Subiendo ${i + 1}/${attachmentFiles.length}...`);
        try {
          const fd = new FormData();
          fd.append('file', file);
          const res = await apiFetch(`/api/projects/${id}/attachments`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
            body: fd
          });
          if (res.status === 401) return handleUnauthorized();
          if (!res.ok) {
            const data = await parseJsonSafe(res);
            throw new Error((data && data.message) || 'No se pudo subir');
          }
        } catch (err) {
          errores.push(`${file.name}: ${err.message}`);
        }
      }
      setAttachmentFiles([]);
      setAttachmentInputKey((k) => k + 1);
      if (errores.length) {
        setAttachmentsError(`No se pudieron subir ${errores.length} archivo(s): ${errores.join('; ')}`);
      }
      await fetchAttachments();
    } finally {
      setAttachmentUploading(false);
      setAttachmentProgress('');
    }
  };

  const deleteAttachment = async (attachmentId) => {
    if (!window.confirm('¿Eliminar este adjunto?')) return;
    try {
      const res = await apiFetch(`/api/projects/project-attachments/${attachmentId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo eliminar el adjunto');
      await fetchAttachments();
    } catch (err) {
      alert(err.message);
    }
  };

  const openPreview = async ({ downloadUrl, mime, filename }) => {
    setPreview({ open: true, downloadUrl, url: '', mime: mime || '', filename: filename || 'archivo', loading: true });
    try {
      const res = await apiFetch(downloadUrl, { headers: { Authorization: `Bearer ${token}` } });
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) {
        const data = await parseJsonSafe(res);
        throw new Error((data && data.message) || 'No se pudo cargar la vista previa');
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      setPreview((p) => ({ ...p, url, loading: false }));
    } catch (err) {
      setPreview({ open: false, downloadUrl: '', url: '', mime: '', filename: '', loading: false });
      alert(err.message);
    }
  };

  const closePreview = () => {
    setPreview((p) => {
      if (p.url) window.URL.revokeObjectURL(p.url);
      return { open: false, downloadUrl: '', url: '', mime: '', filename: '', loading: false };
    });
  };

  const openNewFollowup = () => {
    setEditingFollowupId(null);
    setFollowupForm({ titulo: '', fecha: todayStr(), notas: '', visible_cliente: false });
    setFollowupsError('');
    setShowFollowupForm(true);
  };

  const openEditFollowup = (f) => {
    setEditingFollowupId(f.id);
    setFollowupForm({
      titulo: f.titulo || '',
      fecha: f.fecha ? String(f.fecha).slice(0, 10) : '',
      notas: f.notas || '',
      visible_cliente: f.visible_cliente === true
    });
    setFollowupsError('');
    setShowFollowupForm(true);
  };

  const cancelFollowupForm = () => {
    setShowFollowupForm(false);
    setEditingFollowupId(null);
  };

  const saveFollowup = async () => {
    if (!followupForm.titulo.trim()) {
      setFollowupsError('El título del seguimiento es obligatorio');
      return;
    }
    setFollowupSaving(true);
    setFollowupsError('');
    try {
      const isEdit = !!editingFollowupId;
      const url = isEdit ? `/api/projects/followups/${editingFollowupId}` : `/api/projects/${id}/followups`;
      const res = await apiFetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(followupForm)
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error((data && data.message) || 'No se pudo guardar el seguimiento');
      setShowFollowupForm(false);
      setEditingFollowupId(null);
      await fetchFollowups();
    } catch (err) {
      setFollowupsError(err.message);
    } finally {
      setFollowupSaving(false);
    }
  };

  const deleteFollowup = async (followupId) => {
    if (!window.confirm('¿Eliminar este seguimiento y todos sus adjuntos?')) return;
    try {
      const res = await apiFetch(`/api/projects/followups/${followupId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) {
        const d = await parseJsonSafe(res);
        throw new Error((d && d.message) || 'No se pudo eliminar el seguimiento');
      }
      await fetchFollowups();
    } catch (err) {
      alert(err.message);
    }
  };

  const openNewTask = () => {
    setEditingTask(null);
    setTaskForm({ titulo: '', importance: null, estado: 'pendiente', user_id: null, workload: null, client_id: project?.client_id || null, comentarios: '', visible_cliente: false });
    setTasksError('');
    setTaskModalOpen(true);
  };

  const openEditTask = (t) => {
    setEditingTask(t);
    setTaskForm({
      titulo: t.titulo || '',
      importance: t.importance || null,
      estado: t.estado || 'pendiente',
      user_id: t.user_id || null,
      workload: t.workload || null,
      client_id: t.client_id || null,
      comentarios: t.comentarios || '',
      visible_cliente: t.visible_cliente === true
    });
    setTasksError('');
    setTaskModalOpen(true);
  };

  const closeTaskModal = () => {
    setTaskModalOpen(false);
    setEditingTask(null);
  };

  const saveTask = async () => {
    if (!taskForm.titulo.trim() || !taskForm.importance) {
      setTasksError('Título e importancia son obligatorios');
      return;
    }
    setTaskSaving(true);
    setTasksError('');
    try {
      const isEdit = !!editingTask;
      const url = isEdit ? `/api/tasks/${editingTask.id}` : '/api/tasks';
      const payload = { ...taskForm, project_id: Number(id) };
      const res = await apiFetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error((data && data.message) || 'No se pudo guardar la tarea');
      closeTaskModal();
      await fetchTasks();
    } catch (err) {
      setTasksError(err.message);
    } finally {
      setTaskSaving(false);
    }
  };

  // Enviamos la tarea completa para no perder client_id/user_id/comentarios (el PUT no los hace COALESCE)
  const toggleTaskDone = async (task) => {
    const nuevoEstado = task.estado === 'realizada' ? 'pendiente' : 'realizada';
    try {
      const res = await apiFetch(`/api/tasks/${task.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          titulo: task.titulo,
          client_id: task.client_id,
          importance: task.importance,
          user_id: task.user_id,
          estado: nuevoEstado,
          comentarios: task.comentarios,
          workload: task.workload,
          project_id: task.project_id
        })
      });
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) {
        const d = await parseJsonSafe(res);
        throw new Error((d && d.message) || 'No se pudo actualizar la tarea');
      }
      await fetchTasks();
    } catch (err) {
      alert(err.message);
    }
  };

  // Cambia la visibilidad en el área cliente enviando la tarea completa (el PUT no hace COALESCE de varios campos)
  const toggleTaskVisible = async (task) => {
    try {
      const res = await apiFetch(`/api/tasks/${task.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          titulo: task.titulo,
          client_id: task.client_id,
          importance: task.importance,
          user_id: task.user_id,
          estado: task.estado,
          comentarios: task.comentarios,
          workload: task.workload,
          project_id: task.project_id,
          visible_cliente: !task.visible_cliente
        })
      });
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) {
        const d = await parseJsonSafe(res);
        throw new Error((d && d.message) || 'No se pudo actualizar la visibilidad');
      }
      await fetchTasks();
    } catch (err) {
      alert(err.message);
    }
  };

  // Mueve la tarea a otro estado (tablero Kanban) enviando la tarea completa
  const moveTask = async (task, nuevoEstado) => {
    if (!nuevoEstado || nuevoEstado === task.estado) return;
    try {
      const res = await apiFetch(`/api/tasks/${task.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          titulo: task.titulo,
          client_id: task.client_id,
          importance: task.importance,
          user_id: task.user_id,
          estado: nuevoEstado,
          comentarios: task.comentarios,
          workload: task.workload,
          project_id: task.project_id,
          visible_cliente: task.visible_cliente
        })
      });
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) {
        const d = await parseJsonSafe(res);
        throw new Error((d && d.message) || 'No se pudo mover la tarea');
      }
      await fetchTasks();
    } catch (err) {
      alert(err.message);
    }
  };

  const deleteTask = async (taskId) => {
    if (!window.confirm('¿Eliminar esta tarea?')) return;
    try {
      const res = await apiFetch(`/api/tasks/${taskId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) {
        const d = await parseJsonSafe(res);
        throw new Error((d && d.message) || 'No se pudo eliminar la tarea');
      }
      await fetchTasks();
    } catch (err) {
      alert(err.message);
    }
  };

  return (
    <div className="small">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h2 className="mb-0">Editar proyecto</h2>
          {project && (
            <div className="text-muted">
              {project.titulo} {project.client_name ? (
                <>· {project.client_id ? <Link to={`/clientes/${project.client_id}`}>{project.client_name}</Link> : project.client_name}</>
              ) : ''}
            </div>
          )}
        </div>
        <div className="d-flex gap-2">
          <Link className="btn btn-outline-secondary btn-sm" to="/proyectos">Volver</Link>
          <button className="btn btn-outline-secondary btn-sm" type="button" onClick={() => setActiveTab('tareas')}>Ver tareas</button>
          <button
            className="btn btn-outline-danger btn-sm"
            type="button"
            onClick={async () => {
              if (!window.confirm('¿Eliminar este proyecto y todo su contenido (seguimiento, adjuntos, tareas asociadas se desvinculan)? Esta acción no se puede deshacer.')) return;
              try {
                const res = await apiFetch(`/api/projects/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
                if (res.status === 401) return handleUnauthorized();
                if (!res.ok) { const d = await parseJsonSafe(res); throw new Error((d && d.message) || 'No se pudo eliminar el proyecto'); }
                navigate('/proyectos');
              } catch (e) { alert(e.message); }
            }}
          >
            Eliminar
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {loading ? (
        <p>Cargando...</p>
      ) : (
        <>
          <ul className="nav nav-tabs mb-3 bg-white" style={{ position: 'sticky', top: 0, zIndex: 1010 }} aria-label="Secciones del proyecto">
            <li className="nav-item">
              <button type="button" className={`nav-link ${activeTab === 'info' ? 'active' : ''}`} onClick={() => setActiveTab('info')}>
                Información
              </button>
            </li>
            <li className="nav-item">
              <button type="button" className={`nav-link ${activeTab === 'adjuntos' ? 'active' : ''}`} onClick={() => setActiveTab('adjuntos')}>
                Adjuntos {attachments.length ? <span className="badge bg-secondary ms-1">{attachments.length}</span> : null}
              </button>
            </li>
            <li className="nav-item">
              <button type="button" className={`nav-link ${activeTab === 'seguimiento' ? 'active' : ''}`} onClick={() => setActiveTab('seguimiento')}>
                Seguimiento {followups.length ? <span className="badge bg-secondary ms-1">{followups.length}</span> : null}
              </button>
            </li>
            <li className="nav-item">
              <button type="button" className={`nav-link ${activeTab === 'tareas' ? 'active' : ''}`} onClick={() => setActiveTab('tareas')}>
                Tareas {tasks.length ? <span className="badge bg-secondary ms-1">{tasks.length}</span> : null}
              </button>
            </li>
          </ul>

          {activeTab === 'info' && (
            <div className="card shadow-sm">
              <div className="card-body">
                <form onSubmit={saveProject}>
                  <div className="row g-3">
                    <div className="col-md-8">
                      <label className="form-label">Título *</label>
                      <input className="form-control" value={form.titulo} onChange={(e) => setForm((p) => ({ ...p, titulo: e.target.value }))} required />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Estado</label>
                      <Select
                        options={statusOptions}
                        value={statusOptions.find((o) => o.value === form.estado) || statusOptions[0]}
                        onChange={(opt) => setForm((p) => ({ ...p, estado: opt ? opt.value : 'a_la_espera' }))}
                      />
                    </div>
                    <div className="col-md-8">
                      <label className="form-label">Cliente</label>
                      <Select
                        options={clientOptions}
                        value={clientOptions.find((o) => o.value === form.client_id) || null}
                        onChange={(opt) => setForm((p) => ({ ...p, client_id: opt ? opt.value : null }))}
                        isClearable
                        placeholder="Selecciona cliente"
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Presupuesto (EUR)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className="form-control"
                        value={form.presupuesto}
                        onChange={(e) => setForm((p) => ({ ...p, presupuesto: e.target.value }))}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Tipo</label>
                      <select className="form-select" value={form.tipo} onChange={(e) => setForm((p) => ({ ...p, tipo: e.target.value }))}>
                        {tipoOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Prioridad</label>
                      <select className="form-select" value={form.prioridad} onChange={(e) => setForm((p) => ({ ...p, prioridad: e.target.value }))}>
                        {prioridadOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </div>
                    <div className="col-md-2">
                      <label className="form-label">Inicio</label>
                      <input type="date" className="form-control" value={form.fecha_inicio} onChange={(e) => setForm((p) => ({ ...p, fecha_inicio: e.target.value }))} />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label">Fin</label>
                      <input type="date" className="form-control" value={form.fecha_fin} onChange={(e) => setForm((p) => ({ ...p, fecha_fin: e.target.value }))} />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Usuarios asignados</label>
                      <Select
                        isMulti
                        options={userOptions}
                        value={userOptions.filter((o) => form.assigned_user_ids.includes(o.value))}
                        onChange={(opts) => setForm((p) => ({ ...p, assigned_user_ids: opts ? opts.map((o) => o.value) : [] }))}
                        placeholder="Selecciona uno o varios"
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Comentarios (opcional)</label>
                      <textarea
                        className="form-control"
                        rows="3"
                        value={form.comentarios}
                        onChange={(e) => setForm((p) => ({ ...p, comentarios: e.target.value }))}
                        placeholder="Notas internas del proyecto"
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Notas rápidas</label>
                      <textarea
                        className="form-control"
                        rows="3"
                        value={form.notas}
                        onChange={(e) => setForm((p) => ({ ...p, notas: e.target.value }))}
                        placeholder="Notas breves"
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Links</label>
                      <textarea
                        className="form-control"
                        rows="2"
                        value={form.links}
                        onChange={(e) => setForm((p) => ({ ...p, links: e.target.value }))}
                        placeholder="Pega aquí URLs"
                      />
                    </div>
                    <div className="col-12">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          id="visibleClienteSwitch"
                          checked={form.visible_cliente}
                          onChange={(e) => setForm((p) => ({ ...p, visible_cliente: e.target.checked }))}
                        />
                        <label className="form-check-label" htmlFor="visibleClienteSwitch">
                          Visible en el área de cliente
                        </label>
                      </div>
                      <div className="form-text">Si lo desmarcas, el cliente no verá este proyecto en su área.</div>
                    </div>
                  </div>
                  <div className="d-flex justify-content-end gap-2 mt-3">
                    <button type="button" className="btn btn-outline-secondary" onClick={() => navigate('/proyectos')}>Cancelar</button>
                    <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {activeTab === 'adjuntos' && (
            <div className="card shadow-sm">
              <div className="card-body">
                <h5 className="mb-1">Adjuntos del proyecto</h5>
                <div className="text-muted small mb-3">Puedes seleccionar varios archivos a la vez. Se guardan en la carpeta de Google Drive del proyecto.</div>
                <div className="d-flex gap-2 align-items-center" style={{ maxWidth: 640 }}>
                  <input
                    key={attachmentInputKey}
                    type="file"
                    multiple
                    className="form-control form-control-sm"
                    onChange={(e) => setAttachmentFiles(Array.from(e.target.files || []))}
                  />
                  <button className="btn btn-sm btn-primary flex-shrink-0" type="button" disabled={!attachmentFiles.length || attachmentUploading} onClick={uploadAttachment}>
                    {attachmentUploading
                      ? (attachmentProgress || 'Subiendo...')
                      : (attachmentFiles.length > 1 ? `Subir ${attachmentFiles.length}` : 'Subir')}
                  </button>
                </div>
                {attachmentFiles.length > 0 && !attachmentUploading && (
                  <div className="text-muted small mt-1">{attachmentFiles.length} archivo(s) seleccionado(s).</div>
                )}
                {attachmentsLoading && <div className="text-muted small mt-2">Cargando...</div>}
                {attachmentsError && <div className="text-danger small mt-2">{attachmentsError}</div>}
                {!attachmentsLoading && !attachments.length ? (
                  <div className="text-muted small mt-3">Sin adjuntos.</div>
                ) : (
                  <div className="table-responsive mt-3">
                    <table className="table table-sm align-middle">
                      <thead>
                        <tr>
                          <th>Archivo</th>
                          <th className="text-end"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {attachments.map((f) => (
                          <tr key={f.id}>
                            <td>
                              <div className="fw-semibold">{f.filename}</div>
                              <div className="text-muted small">
                                {formatBytes(f.size)}
                                {f.created_at ? ` · ${new Date(f.created_at).toLocaleString()}` : ''}
                              </div>
                            </td>
                            <td className="text-end">
                              <div className="btn-group btn-group-sm">
                                {canPreview(f.mime_type) && (
                                  <button
                                    className="btn btn-outline-secondary"
                                    type="button"
                                    onClick={() => openPreview({ downloadUrl: `/api/projects/project-attachments/${f.id}/download`, mime: f.mime_type, filename: f.filename })}
                                  >
                                    Ver
                                  </button>
                                )}
                                <button
                                  className="btn btn-outline-primary"
                                  type="button"
                                  onClick={() => downloadFile(`/api/projects/project-attachments/${f.id}/download`, f.filename)}
                                >
                                  Descargar
                                </button>
                                <button
                                  className="btn btn-outline-danger"
                                  type="button"
                                  onClick={() => deleteAttachment(f.id)}
                                >
                                  Eliminar
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'seguimiento' && (
            <div>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h5 className="mb-0">Seguimiento del proyecto</h5>
                  <div className="text-muted small">Anota reuniones con el cliente, avances y cambios acordados. Cada entrada admite adjuntos.</div>
                </div>
                {!showFollowupForm && (
                  <button className="btn btn-primary btn-sm flex-shrink-0" type="button" onClick={openNewFollowup}>+ Añadir seguimiento</button>
                )}
              </div>

              {showFollowupForm && (
                <div className="card shadow-sm mb-3 border-primary">
                  <div className="card-body">
                    <h6 className="mb-3">{editingFollowupId ? 'Editar seguimiento' : 'Nuevo seguimiento'}</h6>
                    <div className="row g-2">
                      <div className="col-md-8">
                        <label className="form-label">Título *</label>
                        <input
                          className="form-control"
                          value={followupForm.titulo}
                          onChange={(e) => setFollowupForm((p) => ({ ...p, titulo: e.target.value }))}
                          placeholder="Ej. Reunión inicial, Cambios en la home..."
                        />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Fecha</label>
                        <input
                          type="date"
                          className="form-control"
                          value={followupForm.fecha}
                          onChange={(e) => setFollowupForm((p) => ({ ...p, fecha: e.target.value }))}
                        />
                      </div>
                    </div>
                    <div className="mt-2">
                      <label className="form-label">Notas</label>
                      <textarea
                        className="form-control"
                        rows="4"
                        value={followupForm.notas}
                        onChange={(e) => setFollowupForm((p) => ({ ...p, notas: e.target.value }))}
                        placeholder="Qué se habló, qué se acordó, próximos pasos..."
                      />
                    </div>
                    <div className="form-check form-switch mt-3">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="followupVisibleSwitch"
                        checked={followupForm.visible_cliente === true}
                        onChange={(e) => setFollowupForm((p) => ({ ...p, visible_cliente: e.target.checked }))}
                      />
                      <label className="form-check-label" htmlFor="followupVisibleSwitch">
                        Mostrar este seguimiento al cliente en su área
                      </label>
                    </div>
                    <div className="d-flex justify-content-end gap-2 mt-3">
                      <button className="btn btn-outline-secondary" type="button" onClick={cancelFollowupForm}>Cancelar</button>
                      <button className="btn btn-primary" type="button" disabled={followupSaving} onClick={saveFollowup}>
                        {followupSaving ? 'Guardando...' : 'Guardar'}
                      </button>
                    </div>
                    {!editingFollowupId && (
                      <div className="text-muted small mt-2">Podrás adjuntar archivos a este seguimiento una vez guardado.</div>
                    )}
                  </div>
                </div>
              )}

              {followupsError && <div className="alert alert-danger">{followupsError}</div>}

              {followupsLoading ? (
                <p className="text-muted">Cargando seguimientos...</p>
              ) : followups.length === 0 ? (
                !showFollowupForm && (
                  <div className="text-center text-muted py-4 border rounded bg-light">
                    Aún no hay seguimientos. Pulsa <strong>"Añadir seguimiento"</strong> para registrar la primera anotación.
                  </div>
                )
              ) : (
                followups.map((f) => (
                  <FollowupCard
                    key={f.id}
                    followup={f}
                    token={token}
                    downloadFile={downloadFile}
                    onPreview={openPreview}
                    onEdit={openEditFollowup}
                    onDelete={deleteFollowup}
                    onChanged={fetchFollowups}
                    onUnauthorized={handleUnauthorized}
                  />
                ))
              )}
            </div>
          )}

          {activeTab === 'tareas' && (
            <div>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h5 className="mb-0">Tareas del proyecto</h5>
                  <div className="text-muted small">Tareas asociadas a este proyecto. También aparecen en /tareas.</div>
                </div>
                <button className="btn btn-primary btn-sm flex-shrink-0" type="button" onClick={openNewTask}>+ Nueva tarea</button>
              </div>

              {tasks.length > 0 && (
                <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                  <div className="pb-progress-wrap">
                    <div className="pb-progress-label">
                      {tasks.filter((t) => t.estado === 'realizada').length}/{tasks.length} completadas
                      {' · '}{tasks.length ? Math.round((tasks.filter((t) => t.estado === 'realizada').length / tasks.length) * 100) : 0}%
                    </div>
                    <div className="pb-progress">
                      <div
                        className="pb-progress-bar"
                        style={{ width: `${tasks.length ? Math.round((tasks.filter((t) => t.estado === 'realizada').length / tasks.length) * 100) : 0}%` }}
                      />
                    </div>
                  </div>
                  <div className="btn-group btn-group-sm" role="group">
                    <button type="button" className={`btn ${taskView === 'list' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setTaskView('list')}>Lista</button>
                    <button type="button" className={`btn ${taskView === 'board' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setTaskView('board')}>Tablero</button>
                  </div>
                </div>
              )}

              {tasksError && <div className="alert alert-danger">{tasksError}</div>}

              {tasksLoading ? (
                <p className="text-muted">Cargando tareas...</p>
              ) : tasks.length === 0 ? (
                <div className="text-center text-muted py-4 border rounded bg-light">
                  No hay tareas asociadas. Pulsa <strong>"Nueva tarea"</strong> para crear la primera.
                </div>
              ) : taskView === 'board' ? (
                <div className="pb-board">
                  {TASK_STATES.map((st) => {
                    const colTasks = tasks.filter((t) => (t.estado || 'pendiente') === st);
                    return (
                      <div key={st} className="pb-col">
                        <div className="pb-col-head">
                          <span className={`pb-dot pb-dot-${st}`} />
                          {estadoLabel[st]}
                          <span className="pb-col-count">{colTasks.length}</span>
                        </div>
                        <div className="pb-col-body">
                          {colTasks.length === 0 ? (
                            <div className="pb-col-empty">Sin tareas</div>
                          ) : colTasks.map((t) => {
                            const idx = TASK_STATES.indexOf(st);
                            return (
                              <div key={t.id} className="pb-card" style={{ borderLeft: `3px solid ${importanceBorder[t.importance] || '#adb5bd'}` }}>
                                <div className="pb-card-title">{t.titulo}</div>
                                <div className="pb-card-meta">
                                  <span className={`badge bg-${taskBadgeColor[t.importance] || 'secondary'}`}>{t.importance || 's/imp.'}</span>
                                  <span className="pb-assignee">{t.user_name || t.user_email || 'Sin asignar'}</span>
                                </div>
                                <div className="pb-card-foot">
                                  <div className="pb-move">
                                    <button type="button" className="btn btn-sm btn-light" disabled={idx === 0} title="Mover atrás" onClick={() => moveTask(t, TASK_STATES[idx - 1])}>←</button>
                                    <button type="button" className="btn btn-sm btn-light" disabled={idx === TASK_STATES.length - 1} title="Mover adelante" onClick={() => moveTask(t, TASK_STATES[idx + 1])}>→</button>
                                    <button type="button" className="btn btn-sm btn-light" title="Editar" onClick={() => openEditTask(t)}>✎</button>
                                  </div>
                                  <div className="form-check form-switch m-0" title="Mostrar al cliente en su área">
                                    <input className="form-check-input" type="checkbox" role="switch" id={`bvis-${t.id}`} checked={t.visible_cliente === true} onChange={() => toggleTaskVisible(t)} />
                                    <label className="form-check-label small" htmlFor={`bvis-${t.id}`}>{t.visible_cliente ? '👁' : ''}</label>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="row g-3">
                  {tasks.map((t) => (
                    <div key={t.id} className="col-md-6 col-xl-4">
                      <div className={`card shadow-sm h-100 ${t.estado === 'realizada' ? 'opacity-75' : ''}`} style={{ borderTop: `4px solid ${importanceBorder[t.importance] || '#adb5bd'}` }}>
                        <div className="card-body d-flex flex-column">
                          <div className="d-flex justify-content-between align-items-start">
                            <h6 className="card-title mb-0" style={{ textDecoration: t.estado === 'realizada' ? 'line-through' : 'none' }}>{t.titulo}</h6>
                            <span className={`badge flex-shrink-0 ms-2 ${estadoBadgeClass[t.estado] || 'bg-light text-dark'}`}>{estadoLabel[t.estado] || t.estado}</span>
                          </div>
                          <div className="text-muted small mt-1">
                            <span className={`badge bg-${taskBadgeColor[t.importance] || 'secondary'} me-1`}>{t.importance || 's/imp.'}</span>
                            {t.user_name || t.user_email || 'Sin asignar'}
                            {t.client_name ? ` · ${t.client_name}` : ''}
                          </div>
                          {t.comentarios && <div className="small mt-2"><ExpandableText text={t.comentarios} title={t.titulo} /></div>}
                          <div className="form-check form-switch mt-2">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              role="switch"
                              id={`tvis-${t.id}`}
                              checked={t.visible_cliente === true}
                              onChange={() => toggleTaskVisible(t)}
                            />
                            <label className="form-check-label small" htmlFor={`tvis-${t.id}`}>
                              {t.visible_cliente
                                ? <span className="text-success">👁 Visible para el cliente</span>
                                : <span className="text-muted">Solo interno</span>}
                            </label>
                          </div>
                          <div className="d-flex flex-wrap gap-2 mt-3 pt-2 border-top">
                            <button
                              className={`btn btn-sm ${t.estado === 'realizada' ? 'btn-outline-secondary' : 'btn-outline-success'}`}
                              type="button"
                              onClick={() => toggleTaskDone(t)}
                            >
                              {t.estado === 'realizada' ? 'Reabrir' : '✓ Realizada'}
                            </button>
                            <button className="btn btn-sm btn-outline-primary" type="button" onClick={() => openEditTask(t)}>Editar</button>
                            <button className="btn btn-sm btn-outline-danger" type="button" onClick={() => deleteTask(t.id)}>Eliminar</button>
                          </div>
                          <TaskAttachments
                            taskId={t.id}
                            attachments={t.attachments}
                            token={token}
                            onChanged={fetchTasks}
                            onUnauthorized={handleUnauthorized}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {preview.open && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.6)' }} onClick={closePreview}>
          <div className="modal-dialog modal-xl modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title text-truncate" title={preview.filename}>{preview.filename}</h5>
                <button className="btn-close" type="button" onClick={closePreview}></button>
              </div>
              <div className="modal-body text-center" style={{ minHeight: 220 }}>
                {preview.loading && <div className="text-muted py-4">Cargando vista previa...</div>}
                {!preview.loading && preview.url && (
                  (preview.mime || '').toLowerCase().startsWith('image/') ? (
                    <img src={preview.url} alt={preview.filename} style={{ maxWidth: '100%', maxHeight: '75vh', objectFit: 'contain' }} />
                  ) : (
                    <iframe title={preview.filename} src={preview.url} style={{ width: '100%', height: '75vh', border: 'none' }} />
                  )
                )}
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-outline-primary"
                  type="button"
                  onClick={() => downloadFile(preview.downloadUrl, preview.filename)}
                >
                  Descargar
                </button>
                <button className="btn btn-outline-secondary" type="button" onClick={closePreview}>Cerrar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {taskModalOpen && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingTask ? 'Editar tarea' : 'Nueva tarea'}</h5>
                <button className="btn-close" type="button" onClick={closeTaskModal}></button>
              </div>
              <div className="modal-body">
                {tasksError && <div className="alert alert-danger">{tasksError}</div>}
                <div className="row g-2">
                  <div className="col-12">
                    <label className="form-label">Título *</label>
                    <input className="form-control" value={taskForm.titulo} onChange={(e) => setTaskForm((p) => ({ ...p, titulo: e.target.value }))} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Importancia *</label>
                    <Select
                      options={importanceOptions}
                      value={importanceOptions.find((o) => o.value === taskForm.importance) || null}
                      onChange={(opt) => setTaskForm((p) => ({ ...p, importance: opt ? opt.value : null }))}
                      placeholder="Selecciona"
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Estado</label>
                    <Select
                      options={taskEstadoOptions}
                      value={taskEstadoOptions.find((o) => o.value === taskForm.estado) || taskEstadoOptions[0]}
                      onChange={(opt) => setTaskForm((p) => ({ ...p, estado: opt ? opt.value : 'pendiente' }))}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Carga (1-8)</label>
                    <Select
                      options={workloadOptions}
                      isClearable
                      value={workloadOptions.find((o) => o.value === taskForm.workload) || null}
                      onChange={(opt) => setTaskForm((p) => ({ ...p, workload: opt ? opt.value : null }))}
                      placeholder="—"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Usuario asignado</label>
                    <Select
                      options={userOptions}
                      isClearable
                      value={userOptions.find((o) => o.value === taskForm.user_id) || null}
                      onChange={(opt) => setTaskForm((p) => ({ ...p, user_id: opt ? opt.value : null }))}
                      placeholder="Sin asignar"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Cliente</label>
                    <Select
                      options={clientOptions}
                      isClearable
                      value={clientOptions.find((o) => o.value === taskForm.client_id) || null}
                      onChange={(opt) => setTaskForm((p) => ({ ...p, client_id: opt ? opt.value : null }))}
                      placeholder="Sin cliente"
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label">Comentarios</label>
                    <textarea className="form-control" rows="3" value={taskForm.comentarios} onChange={(e) => setTaskForm((p) => ({ ...p, comentarios: e.target.value }))} />
                  </div>
                  <div className="col-12">
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="taskVisibleSwitch"
                        checked={taskForm.visible_cliente === true}
                        onChange={(e) => setTaskForm((p) => ({ ...p, visible_cliente: e.target.checked }))}
                      />
                      <label className="form-check-label" htmlFor="taskVisibleSwitch">
                        Mostrar esta tarea al cliente en su área
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" type="button" onClick={closeTaskModal}>Cancelar</button>
                <button className="btn btn-primary" type="button" disabled={taskSaving} onClick={saveTask}>{taskSaving ? 'Guardando...' : 'Guardar'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectFormPage;
